// apps/client/src/characters/curator.ts (M2-T10; characters pass 2026-10-16) — Professor Reed, an original heron curator (D-20), on Kenney's
// CC0 rig with the doll part toolkit (§8.5), never through `DollSpec`. Sizes are in rig units (RIG_HEIGHT), like doll.ts: a slender grey-blue
// bird in a plum waistcoat and a bow tie, an S-shaped neck up to a small head with round spectacles, a long dagger beak and a slate crest,
// folded wings for arms and long thin legs on three-toed feet. Baked into one skinned mesh like every doll.
import * as THREE from 'three';
import type { AssetService } from '../assets/service';
import { bakeDoll, bakedToyMaterial, disposeDoll, hideRigMeshes, RIG_HEIGHT, type DollBone } from './doll';
import { group, part, shade } from './part';
import { blob, lathe, taperedTube, teardrop } from './shapes';

const C = { feathers: '#B8C4CF', chest: '#EEF1F3', beak: '#E3B04B', legs: '#D9A441', crest: '#5E6E7E', vest: '#7A5C8E', specs: '#3B2A20',
  bow: '#B5452F', ink: '#2B2118' };
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
const Y = v(0, 1, 0);
function slot(rig: THREE.Object3D, bone: DollBone): THREE.Group {
  const b = rig.getObjectByName(bone);
  if (!b) throw new Error(`rig has no bone ${bone}`);
  return group(b, `heron-${bone}`);
}
/** A drop from `from` pointing along `dir` (its base at `from`). */
function drop(parent: THREE.Object3D, len: number, r: number, hex: string, from: [number, number, number], dir: THREE.Vector3, name = '', peak?: number): THREE.Mesh {
  const m = part(parent, teardrop(len, r, 8, 5, peak), hex, from, name);
  m.quaternion.setFromUnitVectors(Y, dir.clone().normalize());
  return m;
}

/** Dresses `rig` as Professor Reed (parts named for tests: heronHead, heronNeck, beak, specs, crest, vest, eye-left/right). */
export function dressCurator(rig: THREE.Object3D): void {
  hideRigMeshes(rig);
  const torso = slot(rig, 'torso');
  const bodyProfile: [number, number][] = [[0, -0.02], [0.06, -0.012], [0.095, 0.02], [0.108, 0.06], [0.1, 0.11], [0.076, 0.15], [0.042, 0.176], [0, 0.182]];
  part(torso, lathe(bodyProfile, 16), C.feathers, [0, 0, 0], 'heronBody').scale.set(1, 1, 1.1);
  part(torso, blob(0.062, 0.08, 0.04, 10, 8), C.chest, [0, 0.1, 0.088]);
  // The waistcoat wraps the body, open down the front where the white chest shows; two buttons and a bow tie.
  const vest = part(torso, lathe([[0.1, 0.03], [0.114, 0.042], [0.115, 0.07], [0.106, 0.108], [0.084, 0.142]], 16, 0.32, Math.PI * 2 - 0.64), C.vest, [0, 0, 0], 'vest');
  vest.scale.set(1, 1, 1.1);
  for (const y of [0.04, 0.08]) part(torso, blob(0.008, 0.008, 0.006, 6, 4), '#E9D8A6', [0.04, y, 0.118]);
  const bow = group(torso, 'bowTie', [0, 0.163, 0.05]);
  for (const sx of [-1, 1]) drop(bow, 0.034, 0.016, C.bow, [0, 0, 0], v(sx, 0.1, 0.2), '', 0.7);
  part(bow, blob(0.011, 0.011, 0.01, 6, 4), shade(C.bow, 0.85));
  for (const sx of [-1, 1]) drop(torso, 0.1, 0.026, shade(C.feathers, 0.9), [sx * 0.02, 0.05, -0.09], v(sx * 0.15, -0.5, -1));   // tail feathers

  // The neck rises from the shoulders in an S; it and the head ride the head bone, so nods turn the whole neck from the shoulders.
  const head = slot(rig, 'head');
  part(head, taperedTube([v(0, -0.03, 0), v(0, 0.06, 0.035), v(0, 0.13, 0.0), v(0, 0.2, 0.025)], (t) => 0.034 - 0.01 * t, 12, 7), C.feathers, [0, 0, 0], 'heronNeck');
  const hc: [number, number, number] = [0, 0.225, 0.035];
  part(head, blob(0.058, 0.054, 0.07, 12, 8), C.feathers, hc, 'heronHead');
  drop(head, 0.18, 0.021, C.beak, [0, 0.215, 0.085], v(0, -0.12, 1), 'beak', 0.12);
  const crest = group(head, 'crest');
  drop(crest, 0.12, 0.012, C.crest, [0, 0.255, 0.0], v(0, 0.35, -1));
  drop(crest, 0.09, 0.01, C.crest, [0, 0.245, -0.005], v(0, 0.1, -1));
  const specs = group(head, 'specs', [0, 0.236, 0.092]);
  for (const [side, sx] of [['left', 1], ['right', -1]] as const) {
    const at: [number, number, number] = [sx * 0.033, 0.236, 0.082];
    part(head, blob(0.013, 0.015, 0.008, 8, 6), C.ink, at, `eye-${side}`);
    part(head, blob(0.004, 0.004, 0.003, 4, 3), '#FFFFFF', [at[0] + 0.004, at[1] + 0.006, at[2] + 0.007]);
    const lens = part(specs, new THREE.TorusGeometry(0.021, 0.0042, 4, 14), C.specs, [sx * 0.033, 0, 0]);
    lens.rotation.y = sx * 0.35;
  }
  part(specs, new THREE.CapsuleGeometry(0.003, 0.02, 2, 4), C.specs, [0, 0.004, 0.006]).rotation.z = Math.PI / 2;

  // Folded wings for arms, darker at the tips; long thin legs on three-toed feet.
  for (const [bone, sx] of [['arm-left', 1], ['arm-right', -1]] as const) {
    const arm = slot(rig, bone);
    arm.position.set(sx * 0.035, 0, 0);
    arm.rotation.z = sx * 0.2;
    const wing = drop(arm, 0.17, 0.05, C.feathers, [0, 0.02, -0.01], v(0, -1, -0.14), '', 0.3);
    wing.scale.x = 0.42;
    const tip = drop(arm, 0.075, 0.03, C.crest, [sx * 0.004, -0.085, -0.025], v(0, -1, -0.2), '', 0.25);
    tip.scale.x = 0.4;
  }
  for (const bone of ['leg-left', 'leg-right'] as const) {
    const leg = slot(rig, bone);
    part(leg, new THREE.CapsuleGeometry(0.013, 0.15, 2, 6), C.legs, [0, -0.085, 0]);
    for (const a of [-0.45, 0, 0.45]) {
      const toe = part(leg, new THREE.CapsuleGeometry(0.008, 0.045, 2, 5), C.legs, [Math.sin(a) * 0.025, -0.17, 0.004 + Math.cos(a) * 0.025]);
      toe.quaternion.setFromUnitVectors(Y, v(Math.sin(a), -0.08, Math.cos(a)).normalize());
    }
  }
}

export async function createCurator(assets: Pick<AssetService, 'rig'>, height: number):
  Promise<{ root: THREE.Group; mixer: THREE.AnimationMixer; update(dt: number): void; dispose(): void }> {
  const rig = await assets.rig();
  dressCurator(rig.scene);
  bakeDoll(rig.scene, bakedToyMaterial());          // one draw call per pass; unpatched, like the toy materials it replaces
  const root = new THREE.Group();
  rig.scene.scale.setScalar(height / RIG_HEIGHT);
  root.add(rig.scene);
  const mixer = new THREE.AnimationMixer(rig.scene);
  const idle = rig.clips.find((c) => c.name === 'idle');
  if (idle) mixer.clipAction(idle).play();
  return { root, mixer, update: (dt) => mixer.update(dt), dispose: () => { mixer.stopAllAction(); mixer.uncacheRoot(rig.scene); disposeDoll(root); } };
}
